// components/Dashboard/tabs/StatisticsTab.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './StatisticsTab.css';

const StatisticsTab = ({
  statistics,
  channels,
  channelStats,
  loadingStats,
  mtprotoStatus,
  onFetchChannelStats,
  onRefreshChannels
}) => {
  const [previousStats, setPreviousStats] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const isInitialMount = useRef(true);
  const previousStatsRef = useRef({});

  // İlk yüklemede kanalları getir
  useEffect(() => {
    if (isInitialMount.current && channels.length > 0 && Object.keys(channelStats).length === 0) {
      isInitialMount.current = false;
      // Sıralı olarak kanalları yükle
      const loadChannelsSequentially = async () => {
        for (const channel of channels) {
          await onFetchChannelStats(channel.id);
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      };
      loadChannelsSequentially();
    }
  }, [channels, channelStats, onFetchChannelStats]);

  // channelStats değiştiğinde önceki değerleri kaydet
  useEffect(() => {
    Object.keys(channelStats).forEach(channelId => {
      const current = channelStats[channelId];
      const previous = previousStatsRef.current[channelId];
      
      if (current && !previous) {
        // İlk kez veri geldi, kaydet
        previousStatsRef.current[channelId] = { ...current };
      }
    });
    setPreviousStats({ ...previousStatsRef.current });
  }, [channelStats]);

  // İstatistik değişimini hesapla
  const getStatChange = useCallback((channelId, statType) => {
    const current = channelStats[channelId]?.[statType] || 0;
    const previous = previousStats[channelId]?.[statType];
    
    if (previous === undefined || previous === current) {
      return null;
    }
    
    return current - previous;
  }, [channelStats, previousStats]);

  // Tek kanal güncelle
  const handleUpdateSingleChannel = useCallback(async (channelId) => {
    // Güncelleme öncesi mevcut değerleri kaydet
    if (channelStats[channelId]) {
      previousStatsRef.current[channelId] = { ...channelStats[channelId] };
      setPreviousStats(prev => ({
        ...prev,
        [channelId]: { ...channelStats[channelId] }
      }));
    }
    await onFetchChannelStats(channelId);
  }, [channelStats, onFetchChannelStats]);

  // Tüm istatistikleri güncelle
  const handleRefreshAllStats = async () => {
    if (isRefreshing || channels.length === 0) return;
    
    setIsRefreshing(true);
    
    try {
      // Mevcut değerleri önceki olarak kaydet
      channels.forEach(channel => {
        const stats = channelStats[channel.id] || {
          subscriberCount: channel.member_count || 0,
          totalViews: channel.total_views || 0,
          totalForwards: channel.total_forwards || 0,
          totalReactions: channel.total_reactions || 0
        };
        previousStatsRef.current[channel.id] = { ...stats };
      });
      setPreviousStats({ ...previousStatsRef.current });

      // Backend güncelle
      if (onRefreshChannels) {
        await onRefreshChannels();
      }
      
      // MTProto bağlıysa gerçek zamanlı güncelle
      if (mtprotoStatus?.connected) {
        for (const channel of channels) {
          await onFetchChannelStats(channel.id);
          await new Promise(resolve => setTimeout(resolve, 250));
        }
      }
      
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error('İstatistik güncelleme hatası:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Sayı formatlama
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString('tr-TR');
  };

  // Değişim badge'i
  const ChangeBadge = ({ value }) => {
    if (value === null || value === 0) return null;
    const isPositive = value > 0;
    return (
      <span className={`change-badge ${isPositive ? 'positive' : 'negative'}`}>
        {isPositive ? '↑' : '↓'} {Math.abs(value).toLocaleString('tr-TR')}
      </span>
    );
  };

  // Skeleton loader
  const SkeletonCard = () => (
    <div className="stat-card-v2 skeleton">
      <div className="skeleton-header"></div>
      <div className="skeleton-metrics">
        <div className="skeleton-metric"></div>
        <div className="skeleton-metric"></div>
        <div className="skeleton-metric"></div>
        <div className="skeleton-metric"></div>
      </div>
    </div>
  );

  return (
    <div className="statistics-tab-v2">
      {/* Header */}
      <div className="stats-header-v2">
        <div className="header-left">
          <h2>📊 İstatistikler</h2>
          {lastRefreshTime && (
            <span className="last-refresh">
              Son güncelleme: {lastRefreshTime.toLocaleTimeString('tr-TR')}
            </span>
          )}
        </div>
        <button 
          className={`refresh-all-btn ${isRefreshing ? 'loading' : ''}`}
          onClick={handleRefreshAllStats}
          disabled={isRefreshing || channels.length === 0}
        >
          {isRefreshing ? (
            <>
              <span className="spinner"></span>
              Güncelleniyor...
            </>
          ) : (
            <>🔄 Tümünü Güncelle</>
          )}
        </button>
      </div>

      {/* Bağlantı Durumu */}
      <div className={`connection-status-v2 ${mtprotoStatus?.connected ? 'connected' : 'disconnected'}`}>
        <div className="status-icon">
          {mtprotoStatus?.connected ? '🟢' : '🟡'}
        </div>
        <div className="status-text">
          <strong>{mtprotoStatus?.connected ? 'Telegram Bağlı' : 'Telegram Bağlı Değil'}</strong>
          <span>{mtprotoStatus?.connected 
            ? 'Gerçek zamanlı istatistikler aktif' 
            : 'Kayıtlı veriler gösteriliyor'}</span>
        </div>
      </div>

      {/* Özet Kartları */}
      {channels.length > 0 && (
        <div className="summary-cards-v2">
          <div className="summary-card">
            <div className="summary-icon">📡</div>
            <div className="summary-info">
              <span className="summary-value">{channels.length}</span>
              <span className="summary-label">Toplam Kanal</span>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">👥</div>
            <div className="summary-info">
              <span className="summary-value">
                {formatNumber(channels.reduce((sum, ch) => {
                  const stats = channelStats[ch.id] || ch;
                  return sum + (stats.subscriberCount || stats.member_count || 0);
                }, 0))}
              </span>
              <span className="summary-label">Toplam Üye</span>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">👁️</div>
            <div className="summary-info">
              <span className="summary-value">
                {formatNumber(channels.reduce((sum, ch) => {
                  const stats = channelStats[ch.id] || ch;
                  return sum + (stats.totalViews || stats.total_views || 0);
                }, 0))}
              </span>
              <span className="summary-label">Toplam Görüntülenme</span>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">❤️</div>
            <div className="summary-info">
              <span className="summary-value">
                {formatNumber(channels.reduce((sum, ch) => {
                  const stats = channelStats[ch.id] || ch;
                  return sum + (stats.totalReactions || stats.total_reactions || 0);
                }, 0))}
              </span>
              <span className="summary-label">Toplam Reaction</span>
            </div>
          </div>
        </div>
      )}

      {/* Kanal Listesi */}
      <div className="channels-stats-list-v2">
        {channels.length === 0 ? (
          <div className="empty-state-v2">
            <div className="empty-icon">📭</div>
            <h3>Henüz kanal eklenmemiş</h3>
            <p>Kanallar sekmesinden yeni kanal ekleyebilirsiniz</p>
          </div>
        ) : (
          channels.map(channel => {
            const isLoading = loadingStats[channel.id];
            const stats = channelStats[channel.id] || {
              subscriberCount: channel.member_count || 0,
              totalViews: channel.total_views || 0,
              totalForwards: channel.total_forwards || 0,
              totalReactions: channel.total_reactions || 0,
              totalComments: channel.total_comments || 0,
              onlineCount: 0
            };

            if (isLoading && !channelStats[channel.id]) {
              return <SkeletonCard key={channel.id} />;
            }

            return (
              <div key={channel.id} className={`stat-card-v2 ${isLoading ? 'updating' : ''}`}>
                {/* Kart Header */}
                <div className="card-header-v2">
                  <div className="channel-title-v2">
                    <span className="channel-icon">📡</span>
                    <div className="channel-name-wrapper">
                      <h3>{channel.name}</h3>
                      {stats.onlineCount > 0 && (
                        <span className="online-indicator">
                          <span className="online-dot"></span>
                          {stats.onlineCount} çevrimiçi
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className={`update-btn-v2 ${isLoading ? 'loading' : ''}`}
                    onClick={() => handleUpdateSingleChannel(channel.id)}
                    disabled={isLoading || !mtprotoStatus?.connected}
                    title={!mtprotoStatus?.connected ? 'Telegram bağlantısı gerekli' : 'Güncelle'}
                  >
                    {isLoading ? <span className="spinner-small"></span> : '🔄'}
                  </button>
                </div>

                {/* Metrikler */}
                <div className="metrics-grid-v2">
                  <div className="metric-card-v2 subscribers">
                    <div className="metric-header-v2">
                      <span className="metric-icon-v2">👥</span>
                      <span className="metric-label-v2">Üye</span>
                    </div>
                    <div className="metric-value-v2">
                      {formatNumber(stats.subscriberCount)}
                      <ChangeBadge value={getStatChange(channel.id, 'subscriberCount')} />
                    </div>
                  </div>

                  <div className="metric-card-v2 views">
                    <div className="metric-header-v2">
                      <span className="metric-icon-v2">👁️</span>
                      <span className="metric-label-v2">Görüntülenme</span>
                    </div>
                    <div className="metric-value-v2">
                      {formatNumber(stats.totalViews)}
                      <ChangeBadge value={getStatChange(channel.id, 'totalViews')} />
                    </div>
                  </div>

                  <div className="metric-card-v2 forwards">
                    <div className="metric-header-v2">
                      <span className="metric-icon-v2">↗️</span>
                      <span className="metric-label-v2">Paylaşım</span>
                    </div>
                    <div className="metric-value-v2">
                      {formatNumber(stats.totalForwards)}
                      <ChangeBadge value={getStatChange(channel.id, 'totalForwards')} />
                    </div>
                  </div>

                  <div className="metric-card-v2 reactions">
                    <div className="metric-header-v2">
                      <span className="metric-icon-v2">❤️</span>
                      <span className="metric-label-v2">Reaction</span>
                    </div>
                    <div className="metric-value-v2">
                      {formatNumber(stats.totalReactions)}
                      <ChangeBadge value={getStatChange(channel.id, 'totalReactions')} />
                    </div>
                  </div>

                  <div className="metric-card-v2 comments">
                    <div className="metric-header-v2">
                      <span className="metric-icon-v2">💬</span>
                      <span className="metric-label-v2">Yorum</span>
                    </div>
                    <div className="metric-value-v2">
                      {formatNumber(stats.totalComments)}
                    </div>
                  </div>
                </div>

                {/* Veri Kaynağı Bilgisi */}
                {!channelStats[channel.id] && (
                  <div className="data-source-info">
                    ℹ️ Kayıtlı veriler gösteriliyor
                  </div>
                )}

                {/* Son Güncelleme */}
                {stats.lastUpdate && (
                  <div className="card-footer-v2">
                    🕐 {new Date(stats.lastUpdate).toLocaleString('tr-TR')}
                  </div>
                )}

                {/* Son Paylaşımlar */}
                {stats.recentPosts && stats.recentPosts.length > 0 && (
                  <div className="recent-posts-v2">
                    <h4>📮 Son Paylaşımlar</h4>
                    <div className="posts-list-v2">
                      {stats.recentPosts.slice(0, 3).map((post, idx) => (
                        <div key={idx} className="post-item-v2">
                          <div className="post-content">
                            <span className="post-date">
                              {new Date(post.date * 1000).toLocaleDateString('tr-TR')}
                            </span>
                            <span className="post-text">
                              {post.text?.substring(0, 40)}...
                            </span>
                          </div>
                          <div className="post-metrics">
                            <span>👁️ {formatNumber(post.views)}</span>
                            <span>❤️ {formatNumber(post.reactions)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Kanal Performansı */}
      {statistics.channels && statistics.channels.length > 0 && (
        <div className="performance-section-v2">
          <h3>📈 Gönderi Performansı</h3>
          <div className="performance-list">
            {statistics.channels.map(channel => (
              <div key={channel.id} className="performance-row">
                <span className="perf-name">{channel.name}</span>
                <span className="perf-value">{channel.total_posts || 0} gönderi</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsTab;